import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  deleteCall,
  get,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import { debounce } from "lodash";
import MainBody from "../../components/MainBody/MainBody";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import sortCaret from "../../components/SortCaret";
import UploadTask from "./UploadTask";
import AddEditTask from "./AddEditTask";
import { ResponsiveDrawerForm } from "../../components/ResponsiveDrawerForm";
import { LucidePlusCircle } from "lucide-react";
import TaskActionButton from "./TaskActionButton";
import Confirmation from "../../components/Confirmation";
import { useTrust } from "../../lib/context/TrustContext";

const Tasks = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const search = query.get("search");
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const [edit, setEdit] = useState(false);
  const [taskList, setTasksList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [searchParam, setSearchParam] = useState(search || "");
  const [searchParamData, setSearchParamData] = useState(search || "");
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();

  const { trustId } = useTrust();

  const [taskId, setTaskId] = useState(null);
  const [showAddEditTask, setShowAddEditTask] = useState(false);
  const [showTaskDelete, setShowTaskDelete] = useState(false);

  const toggleShowAddEditTask = () => {
    if (showAddEditTask) {
      setEdit(false);
      setTaskId(null);
    }
    setShowAddEditTask(!showAddEditTask);
  };

  const toggleShowDelete = () => {
    if (showTaskDelete) {
      setTaskId(null);
    }
    setShowTaskDelete(!showTaskDelete);
  };

  const fetchTasks = useCallback(
    async (passLimit, passPage) => {
      if (trustId) {
        try {
          setLoading(true);
          const { data } = await get(
            `${API.GET_TASKS}?limit=${
              passLimit ? passLimit : limit ? limit : defaultLimitPagination
            }&page=${passPage ? passPage : page ? page : 1}&search=${
              searchParam ? searchParam : ""
            }&sort_column=${sortField ? sortField : ""}&sort_order=${
              sortOrder ? sortOrder : ""
            }&trust_id=${trustId ? trustId : ""}`
          );

          setLoading(false);
          setLimit(data?.data?.per_page);
          setPage(data?.data?.current_page);
          setTotal(data?.data?.total);
          setFrom(data?.data?.from);
          setTo(data?.data?.to);
          const taskList = data.data.data;
          let notSelectedIds = [];
          for (let i = 0; i < taskList?.length; i++) {
            notSelectedIds.push(taskList[i]?.id);
          }
          // setNotSelectedRowIds(notSelectedIds);
          setTasksList(taskList);
          return data.data;
        } catch (error) {
          setLoading(false);
          setTasksList([]);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParam, sortOrder, sortField, trustId]
  );

  const tasksGenerator = (quantity) => {
    const items = [];
    for (let i = 0; i < quantity; i++) {
      items.push({
        key: taskList[i]?.id,
        job_code: taskList[i]?.ts_job?.code ? taskList[i]?.ts_job?.code : "-",
        title: taskList[i]?.title ? taskList[i]?.title : "-",
        code: taskList[i]?.code ? taskList[i]?.code : "-",
        description: taskList[i]?.description ? taskList[i]?.description : "-",
        action: (
          <>
            <TaskActionButton
              id={taskList[i]?.id}
              setEdit={setEdit}
              setTaskId={setTaskId}
              toggleShowAddEditTask={toggleShowAddEditTask}
              toggleShowDelete={toggleShowDelete}
              used={taskList[i]?.is_used ? true : false}
            />
          </>
        ),
      });
    }
    return items;
  };

  const tasks = tasksGenerator(taskList?.length);

  const columns = [
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "job_code",
      text: "Job Code",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "title",
      text: "Title",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "code",
      text: "Code",
      sort: true,
      sortCaret,
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "description",
      text: "Description",
    },
    {
      headerClasses: THClasses,
      classes: TDClasses,
      dataField: "action",
      text: "Action",
      align: "center",
      headerAlign: "center",
      style: { width: "127px" },
    },
  ];

  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    navigate(
      `?limit=${limit ? limit : defaultLimitPagination}&page=${
        page ? page : 1
      }&search=${searchParam ? searchParam : ""}&sort_column=${
        sortField ? sortField : ""
      }&sort_order=${sortOrder ? sortOrder : ""}`,
      {
        replace: true,
      }
    );
  }, [navigate, limit, searchParam, page, sortField, sortOrder]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    debounce((nextValue) => {
      setSearchParam(nextValue);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (event) => {
    const { value: nextValue } = event.target;
    setSearchParamData(nextValue);
    setPage(1);
    debouncedSave(nextValue);
  };

  const handleClearSearch = () => {
    setSearchParamData();
    setSearchParam("");
  };

  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    setLimit(limit);
    setPage(1);
    fetchTasks(limit, 1);
  };

  const handleChangePage = (page) => {
    setPage(page);
    fetchTasks(limit, page);
  };

  // Single COA Delete
  const handleTaskDelete = async () => {
    try {
      setLoading(true);
      await deleteCall(`${API.TASK_DELETE}/${taskId}`);
      setLoading(false);
      fetchTasks(limit, page);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <>
      <MainBody
        searchParamData={searchParamData}
        handleClearSearch={handleClearSearch}
        handleSearchChange={handleSearchChange}
        mainTitle={"Tasks/Milestones"}
        actionContent={
          <div className="flex items-center gap-3 flex-wrap">
            {/* Import Task */}
            <UploadTask
              fetchData={() => fetchTasks(limit, page)}
              trustId={trustId}
              ButtonName={"Import"}
              setLoading={setLoading}
            />

            {/* Add Task */}
            <ResponsiveDrawerForm
              description={""}
              title={edit ? "Edit Task/Milestone" : "Add /Milestone"}
              handleOpen={() => {}}
              handleClose={() => {
                setEdit(false);
                setTaskId(null);
                setShowAddEditTask(false);
              }}
              buttonText={
                <div className="flex items-center gap-1">
                  <LucidePlusCircle size={18} />
                  <span>Add Task/Milestone</span>
                </div>
              }
              className={"sm:max-w-1/2"}
              open={showAddEditTask}
              buttonClassName={""}
              setOpen={setShowAddEditTask}
              write={true}
            >
              <AddEditTask
                edit={edit}
                toggleShowAddEditTask={toggleShowAddEditTask}
                id={taskId}
                trustId={trustId}
                fetchTaskData={() => fetchTasks(limit, page)}
              />
            </ResponsiveDrawerForm>
          </div>
        }
        loading={loading}
      >
        <div className="custom-table">
          <div className="table-body-class">
            <BootstrapTable
              keyField="key"
              remote
              data={tasks}
              columns={columns}
              onTableChange={handleTableChange}
              rowClasses={TRClasses}
              noDataIndication="No Data Found"
              classes={TableClasses}
              wrapperClasses={TableWrapClasses}
            />
          </div>

          <Pagination
            total={total}
            limit={parseInt(limit)}
            currentPage={page}
            updateLimit={handleUpdateLimit}
            updatePage={handleChangePage}
            from={from}
            to={to}
          />
        </div>
      </MainBody>

      <Confirmation
        title={"Delete Task/Milestone"}
        open={showTaskDelete}
        setOpen={setShowTaskDelete}
        toggleShow={() => {
          toggleShowDelete();
        }}
        className="sm:max-w-1/2"
        message={"Are you sure you want to delete task?"}
        okAction={async () => {
          handleTaskDelete();
          toggleShowDelete();
          // fetchTasks();
        }}
        cancelAction={() => {
          setTaskId(null);
          setShowTaskDelete(!showTaskDelete);
        }}
      />
    </>
  );
};

export default Tasks;
